
import { defineComponent, ref, computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import DynamicGroupForm from '@/components/form/common/DynamicGroupForm.vue'
import { ElForm } from 'element-plus'
import { RadioGroupInstance, GroupField } from '@/components/form/common/DynamicFormModel'
import { formatDecimal } from '@/utils'

export default defineComponent({

  components: {
    DynamicGroupForm
  },

  props: {
    formModel: {
      type: Object,
      default: () => {
        return null
      }
    },
    formStruct: {
      type: Array,
      default: () => [] as any
    },
    formRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    groupsModel: {
      type: Array as PropType<Record<string, any>[]>,
      default: () => []
    },
    groupsStruct: {
      type: Array as () => GroupField[],
      default: () => [] as any
    },
    groupsRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    submitButton: {
      type: String,
      default: 'common.submit'
    },
    resetButton: {
      type: String,
      default: 'common.reset'
    },
    formWidth: {
      type: Number,
      default: 850
    },
    labelWidth: {
      type: String,
      default: '150px'
    },
    groupLabelWidth: {
      type: String,
      default: '100px'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    defaultGroup: {
      type: Boolean,
      default: false
    },
    permitAddGroup: {
      type: Boolean,
      default: true
    }
  },

  emits: ['submit', 'reset', 'groups-change', 'update:formModel'],

  setup(props, context) {
    const { t } = useI18n()
    const cFormRef = ref(ElForm)
    const fM = ref(props.formModel || {})
    const fS = ref(props.formStruct)
    const fR = ref(props.formRules)

    // 动态组相关状态
    const defaultGroupIndex = ref(0)
    let formGroupId = 0

    // 从父组件获取字段配置
    const groupFields = computed<GroupField[]>(() => {
      return props.groupsStruct || []
    })
    const groupFormRules = computed(() => {
      return props.groupsRules || {}
    })
    // 存放动态组的 form refs
    const groupFormRefs = ref<any[]>([])

    // 子组件调用时执行
    const setGroupFormRef = (groupIndex: number, el: any) => {
      if (el) {
        groupFormRefs.value[groupIndex] = el
      } else {
        // 防止 v-for 删除后还保留旧 ref
        groupFormRefs.value.splice(groupIndex, 1)
      }
    }
    // 创建新的信息组
    const createGroup = (model?: Record<string, any>, isDefault = false): RadioGroupInstance => {
      formGroupId++

      const defaultData: Record<string, any> = {}

      // 确保遍历 groupFields 并初始化所有属性
      groupFields.value.forEach(field => {
        defaultData[field.prop] = model?.[field.prop] ?? ''
      })

      return {
        id: formGroupId,
        model: defaultData,
        isDefault: isDefault
      }
    }
    const formGroupsRefs = ref<any[]>([])
    const formGroups = ref<RadioGroupInstance[]>(
      props.groupsModel && props.groupsModel.length > 0
        ? props.groupsModel.map(model => createGroup(model, model.isDefault))
        : props.defaultGroup ? [createGroup()] : []
    )

    // 设置默认选中的 radio
    const setDefaultRadio = () => {
      // 查找第一个 isDefault 为 true 的组
      const defaultIndex = formGroups.value.findIndex(group => group.isDefault === true)
      if (defaultIndex !== -1) {
        defaultGroupIndex.value = defaultIndex
      } else if (formGroups.value.length > 0) {
        // 如果没有设置默认，默认选中第一个
        defaultGroupIndex.value = 0
        formGroups.value[0].isDefault = true
      }
    }
    const addGroup = () => {
      const newGroup = createGroup()
      formGroups.value.push(newGroup)
      if (formGroups.value.length === 1) {
        defaultGroupIndex.value = 0
        formGroups.value[0].isDefault = true
      }
      context.emit('groups-change', formGroups.value.map(group => group.model))
    }

    const removeGroup = (index: number) => {
      if (!props.defaultGroup || formGroups.value.length > 1) {
        // 处理默认组逻辑
        if (defaultGroupIndex.value === index) {
          // 删除的是默认组，清空默认选择
          defaultGroupIndex.value = -1
        } else if (defaultGroupIndex.value > index) {
          // 如果删除的组在默认组之前，调整默认组索引
          defaultGroupIndex.value--
        }

        formGroups.value.splice(index, 1)
        formGroupsRefs.value.splice(index, 1)
        context.emit('groups-change', formGroups.value.map(group => group.model))
      }
    }

    const handleDefaultChange = (index: number) => {
      // 更新所有组的 isDefault 状态
      formGroups.value.forEach((group, i) => {
        group.isDefault = i === index
      })
      defaultGroupIndex.value = index
    }

    // const submitEmit = () => {
    //   (cFormRef.value as typeof ElForm).validate((valid: any) => {
    //     if (valid) {
    //       const submitData = {
    //         ...fM.value,
    //         model: formGroups.value.map(group => ({
    //           ...group.model,
    //           isDefault: group.isDefault || false
    //         }))
    //       }
    //       context.emit('submit', submitData)
    //       console.log('提交数据:', JSON.stringify(submitData))
    //     }
    //   })
    // }
    const submitEmit = async() => {
      // 校验主表单
      const mainValid = await cFormRef.value?.validate().catch(() => false)
      // if (!mainValid) {
      //   console.warn('主表单校验失败')
      //   return
      // }

      // 校验所有动态子表单
      const groupValid = await Promise.all(
        groupFormRefs.value.map(form =>
          form?.validate().catch(() => false)
        )
      )
      if (groupValid.includes(false) || !mainValid) {
        return
      }

      // 所有表单都通过了
      const submitData = {
        ...fM.value,
        model: formGroups.value.map(group => ({
          ...group.model,
          isDefault: group.isDefault
        }))
      }
      context.emit('submit', submitData)
    }

    const resetEmit = () => {
      (cFormRef.value as typeof ElForm).resetFields()
      formGroups.value = []
      formGroupsRefs.value = []
      defaultGroupIndex.value = -1

      if (props.defaultGroup) {
        addGroup()
      }
      formGroups.value = props.groupsModel && props.groupsModel.length > 0
        ? props.groupsModel.map(model => createGroup(model))
        : props.defaultGroup
          ? [createGroup()]
          : []
      context.emit('reset')
    }

    const updateGroupModel = (groupIndex: number, newModel: Record<string, any>) => {
      if (formGroups.value[groupIndex]) {
        formGroups.value[groupIndex].model = newModel
      }
    }

    const getInputType = (item: any) => {
      // 配置了 decimalPlaces 时，强制用 text，方便控制输入
      if (item.component === 'el-input' && item.decimalPlaces !== undefined) {
        return 'text'
      }
      return item.type
    }

    const handleInput = (prop: string, index: number, val: any) => {
      const currentItem = props.formStruct[index] as any

      if (currentItem.component === 'el-input' && (currentItem.decimalPlaces != null || currentItem.isPositive != null)) {
        fM.value[prop] = formatDecimal(val, currentItem.decimalPlaces, currentItem.isPositive)

        context.emit('update:formModel', { ...fM.value })
        return
      }

      fM.value[prop] = val
      context.emit('update:formModel', { ...fM.value })
    }

    // 初始化
    const initGroups = () => {
      if (props.groupsModel && props.groupsModel.length > 0) {
        // 已经有数据，设置默认 radio
        setDefaultRadio()
      }
    }

    initGroups()

    return {
      t,
      fM,
      fS,
      fR,
      cFormRef,
      formGroups,
      groupFields,
      groupFormRules,
      defaultGroupIndex,
      handleDefaultChange,
      updateGroupModel,
      addGroup,
      removeGroup,
      setGroupFormRef,
      submitEmit,
      resetEmit,
      getInputType,
      handleInput
    }
  }
})
